import { produce } from "immer";
import { get, set } from "lodash-es";
/**
 * Creates a Leiten record object that allows managing a specific value within a Store.
 * @template Store - The type of the store object.
 * @template P - The type of the path to the value within the store object.
 * @param {StoreApi<Store>} store - The store object.
 * @param {P extends string
 ? DotNestedValue<Store, P> extends Array<any>
 ? never
 : DotNestedValue<Store, P> extends object | null
 ? P
 : never
 : never} path - The path to the value within the store object.
 * @param {ILeitenRecordEffects<DotNestedValue<Store, P>, Store>} [effects] - Optional effects for the Leiten record.
 * @throws {Error} - Throws an error if the initial value is not an object or "_empty".
 * @returns {ILeitenRecord<DotNestedValue<Store, P>>} - The created Leiten record object.
 */
export const leitenRecord = (store, path, effects) => {
    const initialValue = get(store.getState(), path, "_empty");
    if (initialValue === "_empty" || typeof initialValue !== "object") {
        throw new Error("[leitenRecord] The defined path does not match the required structure");
    }
    const getState = () => {
        const value = get(store.getState(), path, "_empty");
        return value !== "_empty" ? value : initialValue;
    };
    const setState = (next) => {
        const prev = getState();
        const draftState = produce(store.getState(), (draft) => {
            set(draft, path, next);
        });
        const nextState = effects?.patchEffect
            ? { ...draftState, ...effects.patchEffect(next) }
            : draftState;
        store.setState(nextState);
        effects?.sideEffect?.({ prev, next });
    };
    const clear = () => {
        const nextState = produce(store.getState(), (draft) => {
            set(draft, path, initialValue);
        });
        store.setState(nextState);
    };
    const patch = (value) => {
        setState({ ...getState(), ...value });
    };
    return { clear, set: setState, get: getState, patch };
};
// example
// const useAuthStore = create(() => ({
//   // auth: initialContentLoading<IResult, IPayload>(null),
//   test: { a: [{ a: 12 }, { a: 11 }], b: 12 },
//   a: {
//     b: "test",
//   },
// }));
//
// // const token = useAuthStore.getState().test.a
//
// const controller = leitenRecord(useAuthStore, "a");
// controller.patch({ b: "12" });
